/**
 * Maps catalog {@code formCode} values (from chat-flow / invitation) to an in-app fill-in flow.
 * When adding a new definition-driven document, register its form codes here and branch in
 * {@link SubmitDocumentsFlow} (or a future router) for the matching phase / chat component.
 *
 * Keep worker-registry codes aligned with {@code formCodes} in backend
 * {@code form-definitions/worker-registry.json}.
 */
import type { FillInPdfPreviewKind } from '../documents/pdfPreview/fillInPdfPreviewModel';

export type FillInFormFlowId =
  | 'worker-registry'
  | 'skip-fill-in'
  | 'employment-contract'
  | 'pledge'
  | 'password-handling'
  | 'personal-seal'
  | 'previous-employment-inquiry'
  | 'hire-health-survey'
  | 'bank-account-details'
  | 'dependent-deduction-declaration'
  | 'career-history-survey'
  | 'pre-employment-education'
  | 'site-arrival-confirmation'
  | 'career-up-confirmation'
  | 'career-up-application'
  | 'health-info-zuido';

const WORKER_REGISTRY_FORM_CODES: ReadonlySet<string> = new Set(['FF001']);
const EMPLOYMENT_CONTRACT_FORM_CODES: ReadonlySet<string> = new Set(['FF002']);
const PLEDGE_FORM_CODES: ReadonlySet<string> = new Set(['FF004']);
const PASSWORD_HANDLING_FORM_CODES: ReadonlySet<string> = new Set(['FF010']);
const PERSONAL_SEAL_FORM_CODES: ReadonlySet<string> = new Set(['FF013']);
const PREVIOUS_EMPLOYMENT_INQUIRY_FORM_CODES: ReadonlySet<string> = new Set(['FF015']);
const SKIP_FILL_IN_FORM_CODES: ReadonlySet<string> = new Set([]);
const HIRE_HEALTH_SURVEY_FORM_CODES: ReadonlySet<string> = new Set(['FF003']);
const BANK_ACCOUNT_DETAILS_FORM_CODES: ReadonlySet<string> = new Set(['FF005']);
const DEPENDENT_DEDUCTION_FORM_CODES: ReadonlySet<string> = new Set(['FF006']);
const CAREER_HISTORY_SURVEY_FORM_CODES: ReadonlySet<string> = new Set(['FF007']);
const PRE_EMPLOYMENT_EDUCATION_FORM_CODES: ReadonlySet<string> = new Set(['FF014']);
const SITE_ARRIVAL_CONFIRMATION_FORM_CODES: ReadonlySet<string> = new Set(['FF017']);
const CAREER_UP_CONFIRMATION_FORM_CODES: ReadonlySet<string> = new Set(['FF008']);
const CAREER_UP_APPLICATION_FORM_CODES: ReadonlySet<string> = new Set(['FF009']);
const HEALTH_INFO_ZUIDO_FORM_CODES: ReadonlySet<string> = new Set(['FF011']);

function normalizeFormCode(formCode: string | undefined | null): string {
  return formCode?.trim().toUpperCase() ?? '';
}

export function resolveFillInFormFlowId(formCode: string | undefined | null): FillInFormFlowId | null {
  const code = normalizeFormCode(formCode);
  if (!code) return null;
  if (WORKER_REGISTRY_FORM_CODES.has(code)) return 'worker-registry';
  if (EMPLOYMENT_CONTRACT_FORM_CODES.has(code)) return 'employment-contract';
  if (PLEDGE_FORM_CODES.has(code)) return 'pledge';
  if (PASSWORD_HANDLING_FORM_CODES.has(code)) return 'password-handling';
  if (PERSONAL_SEAL_FORM_CODES.has(code)) return 'personal-seal';
  if (PREVIOUS_EMPLOYMENT_INQUIRY_FORM_CODES.has(code)) return 'previous-employment-inquiry';
  if (SKIP_FILL_IN_FORM_CODES.has(code)) return 'skip-fill-in';
  if (HIRE_HEALTH_SURVEY_FORM_CODES.has(code)) return 'hire-health-survey';
  if (BANK_ACCOUNT_DETAILS_FORM_CODES.has(code)) return 'bank-account-details';
  if (DEPENDENT_DEDUCTION_FORM_CODES.has(code)) return 'dependent-deduction-declaration';
  if (CAREER_HISTORY_SURVEY_FORM_CODES.has(code)) return 'career-history-survey';
  if (PRE_EMPLOYMENT_EDUCATION_FORM_CODES.has(code)) return 'pre-employment-education';
  if (SITE_ARRIVAL_CONFIRMATION_FORM_CODES.has(code)) return 'site-arrival-confirmation';
  if (CAREER_UP_CONFIRMATION_FORM_CODES.has(code)) return 'career-up-confirmation';
  if (CAREER_UP_APPLICATION_FORM_CODES.has(code)) return 'career-up-application';
  if (HEALTH_INFO_ZUIDO_FORM_CODES.has(code)) return 'health-info-zuido';
  return null;
}

export function isRegisteredFillInFormCode(formCode: string | undefined | null): boolean {
  return resolveFillInFormFlowId(formCode) != null;
}

/** Maps catalog form code to PDF preview kind for admin / employee preview URLs. */
export function resolveFillInPdfPreviewKind(
  formCode: string | undefined | null
): FillInPdfPreviewKind | null {
  const flow = resolveFillInFormFlowId(formCode);
  if (!flow || flow === 'skip-fill-in') return null;
  switch (flow) {
    case 'dependent-deduction-declaration':
      return 'tax-declaration';
    case 'career-history-survey':
      return 'work-dust-history';
    case 'worker-registry':
    case 'hire-health-survey':
    case 'bank-account-details':
    case 'pre-employment-education':
    case 'site-arrival-confirmation':
    case 'career-up-application':
    case 'health-info-zuido':
    case 'employment-contract':
    case 'pledge':
    case 'password-handling':
    case 'personal-seal':
    case 'previous-employment-inquiry':
      return flow;
    default:
      return null;
  }
}
